import { Col, Row } from '@advanza/grid'
import { SelectCheckbox } from '@advanza/input'
import { Divider } from '@advanza/ui'
import TabsNavigation from '@trustoo/components/ui/TabsNavigation'
import DatePicker from 'components/DatePicker'
import Page from 'components/ui/Page'
import { localToUTC, SQL_DATE_TIME_FORMAT_FNS, utcToLocal } from 'date'
import { useState } from 'react'
import CancellationsList, { useCancellationsList } from './CancellationsList'
import ScheduledTerminationsList, {
    useScheduledTerminationsList,
} from './ScheduledTerminationsList'

const TAB_CHURNED = 'churned'
const TAB_SCHEDULED = 'scheduled'

const SCHEDULED_STATUS_OPTIONS = [
    { name: 'Scheduled', value: 'scheduled' },
    { name: 'Terminated', value: 'terminated' },
    { name: 'Cancelled', value: 'cancelled' },
    { name: 'Superseded', value: 'superseded' },
]

const useDateFilter = (changeFilter) => (e) => {
    const { name, value } = e.target

    if (value) {
        const dateTimeLocalStr =
            value.split(' ')[0] + ' ' + (name === 'startDate' ? '00:00:00' : '23:59:59')
        changeFilter({
            [name]: localToUTC(dateTimeLocalStr, SQL_DATE_TIME_FORMAT_FNS),
            page: 0,
        })
    } else {
        changeFilter({ [name]: null, page: 0 })
    }
}

const DateRangeFilters = ({ filter, onDateChange }) => (
    <>
        <Col x>
            <DatePicker
                onChange={onDateChange}
                dayPickerProps={{
                    disabled: (date) => date > new Date(),
                    showOutsideDays: false,
                }}
                value={utcToLocal(filter.startDate, SQL_DATE_TIME_FORMAT_FNS)}
                name="startDate"
                placeholder="Start date"
            />
        </Col>
        <Col x>
            <DatePicker
                onChange={onDateChange}
                dayPickerProps={{ disabled: (date) => date > new Date() }}
                value={utcToLocal(filter.endDate, SQL_DATE_TIME_FORMAT_FNS)}
                name="endDate"
                placeholder="End date"
            />
        </Col>
    </>
)

const ChurnedTab = ({ filterId }) => {
    const {
        changeFilter,
        filter = {},
        extra: { cancelReasons = {} } = {},
    } = useCancellationsList(filterId)
    const onDateChange = useDateFilter(changeFilter)

    return (
        <>
            <Divider m />
            <Row middle="xs">
                <Col x>
                    <SelectCheckbox
                        value={filter.flags}
                        placeholder="All Cancellations"
                        options={Object.keys(cancelReasons).map((key) => ({
                            name: cancelReasons[key],
                            value: parseInt(key, 10),
                        }))}
                        onChangeValue={(flags) => changeFilter({ flags })}
                    />
                </Col>
                <DateRangeFilters filter={filter} onDateChange={onDateChange} />
            </Row>
            <Divider m />
            <CancellationsList filterId={filterId} />
        </>
    )
}

const ScheduledTab = ({ filterId }) => {
    const {
        changeFilter,
        filter = {},
        extra: { cancelReasons = {} } = {},
    } = useScheduledTerminationsList(filterId)
    const onDateChange = useDateFilter(changeFilter)

    return (
        <>
            <Divider m />
            <Row middle="xs">
                <Col x>
                    <SelectCheckbox
                        value={filter.statuses}
                        placeholder="All statuses"
                        options={SCHEDULED_STATUS_OPTIONS}
                        onChangeValue={(statuses) => changeFilter({ statuses })}
                    />
                </Col>
                <Col x>
                    <SelectCheckbox
                        value={filter.flags}
                        placeholder="All reasons"
                        options={Object.keys(cancelReasons).map((key) => ({
                            name: cancelReasons[key],
                            value: parseInt(key, 10),
                        }))}
                        onChangeValue={(flags) => changeFilter({ flags })}
                    />
                </Col>
                <DateRangeFilters filter={filter} onDateChange={onDateChange} />
            </Row>
            <Divider m />
            <ScheduledTerminationsList filterId={filterId} />
        </>
    )
}

const CancellationsPage = ({ filterId = 'all' }) => {
    const [activeTabId, setActiveTabId] = useState(TAB_CHURNED)

    return (
        <Page title="Cancellations">
            <Divider m />
            <h1>Cancellations</h1>
            <Divider m />
            <TabsNavigation
                activeTabId={activeTabId}
                onTabClick={(tab) => setActiveTabId(tab.id)}
                tabClassName="px-4 py-3 font-semibold aria-selected:text-[#2357B5] aria-[selected=false]:text-[#A4A7AE]"
                tabListClassName="border-b border-solid border-grey-500 w-full"
                tabs={[
                    {
                        id: TAB_CHURNED,
                        label: 'Churned',
                        content: <ChurnedTab filterId={filterId} />,
                    },
                    {
                        id: TAB_SCHEDULED,
                        label: 'Scheduled terminations',
                        content: <ScheduledTab filterId={filterId} />,
                    },
                ]}
            />
        </Page>
    )
}

export default CancellationsPage
